import { record } from "rrweb";
import { Transport } from "./transport";
import { attachInteractions } from "./interactions";
import { attachComments } from "./comments";
import { attachQuestions, type QuestionsHandle } from "./questions";
import { pageKey, layoutHash } from "./page";
import { resolveLocale, makeT } from "./i18n";
import type { InitOptions, SessionMeta, Question } from "./types";

let transport: Transport | undefined;
let stopRecord: (() => void) | undefined;
let detachInteractions: (() => void) | undefined;
let detachComments: (() => void) | undefined;
let questionsHandle: QuestionsHandle | undefined;
let flushTimer: ReturnType<typeof setInterval> | undefined;
let started = false;
let dbg = false;

// Letzter vom Prototyp gemeldeter Zustand (Epic-10-Contract) + kurzer Verlauf der
// letzten High-Level-Schritte — beides wird in Kommentaren als Kontext gespeichert.
let lastReportedState: unknown;
const trail: { t: number; type: string; label: string }[] = [];
const TRAIL_MAX = 6;

function pushTrail(e: { t: number; type: string; target?: string; data?: Record<string, unknown> }): void {
  let label: string = e.type;
  if (e.type === "click") label = `Klick: ${e.target || "?"}`;
  else if (e.type === "route_change" || e.type === "navigate") label = `→ ${String(e.data?.to ?? "")}`;
  else if (e.type === "scroll") label = `Scroll ${e.data?.pct ?? "?"}%`;
  else if (e.type === "input") label = `Eingabe: ${e.target || "?"}`;
  trail.push({ t: e.t, type: e.type, label });
  if (trail.length > TRAIL_MAX) trail.shift();
}

const log = (...args: unknown[]) => {
  if (dbg) console.debug("[userlab]", ...args);
};

function urlParam(name: string): string | undefined {
  if (typeof location === "undefined") return undefined;
  const v = new URLSearchParams(location.search).get(name);
  return v ?? undefined;
}

// ── Beobachtungs-Modus: selbst erzeugte, pro Tab+Studie stabile Session-ID ──
// Liegt in sessionStorage, damit Reloads und SPA-Navigationen innerhalb des
// Tests dieselbe Session fortführen (statt pro Seitenaufruf eine neue zu starten).
function observeKey(studyId: string): string {
  return `userlab.observe.${studyId}`;
}

function readObserveSession(studyId: string): string | undefined {
  try {
    return sessionStorage.getItem(observeKey(studyId)) || undefined;
  } catch {
    return undefined;
  }
}

function ensureObserveSession(studyId: string): string {
  const existing = readObserveSession(studyId);
  if (existing) return existing;
  const id =
    (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
    `obs-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  try {
    sessionStorage.setItem(observeKey(studyId), id);
  } catch {
    /* sessionStorage evtl. blockiert — Session bleibt dann pro Seitenaufruf */
  }
  return id;
}

function buildMeta(): SessionMeta {
  return {
    ua: navigator.userAgent,
    lang: navigator.language,
    screen: { w: screen.width, h: screen.height },
    viewport: { w: window.innerWidth, h: window.innerHeight },
    dpr: window.devicePixelRatio || 1,
    href: location.href,
    pageKey: pageKey(),
    layoutHash: layoutHash(),
  };
}

/**
 * Startet die Aufzeichnung. Idempotent — ein zweiter Aufruf wird ignoriert.
 */
export function init(opts: InitOptions): void {
  if (started) {
    log("init() bereits aufgerufen — ignoriert");
    return;
  }
  if (typeof window === "undefined") return;

  dbg = !!opts.debug;

  // Locale der Widget-Texte: explizit (init.locale/data-locale) → sonst Auto
  // (navigator.language) → "de". Element-Feedback wird beim Init angehängt; das
  // Fragen-Modal kann später study.locale aus der config-Nachricht bevorzugen.
  const baseLocale = resolveLocale({ explicit: opts.locale });

  const key = opts.key ?? urlParam("k");
  const relayUrl =
    opts.relayUrl ??
    (window as unknown as { USERLAB_RELAY?: string }).USERLAB_RELAY ??
    "";

  // Eine moderierte Session kommt IMMER über einen signierten Join-Token (?k=) —
  // dann Interview. Sonst Beobachtung: explizit (data-observe / ?observe=1), bei
  // laufender Tab-Session (Reload), ODER als DEFAULT, sobald ein <script data-study>
  // ohne vorab angelegte Session geladen wird — d. h. das bloße Einbetten des
  // Snippets auf der Live-Seite startet automatisch eine Beobachtungs-Session.
  const explicitSession = opts.sessionId ?? urlParam("session");
  const observe = key
    ? false
    : typeof opts.observe === "boolean"
      ? opts.observe
      : urlParam("observe") === "1" ||
        urlParam("observe") === "true" ||
        (!!opts.studyId && (!!readObserveSession(opts.studyId) || !explicitSession));

  // Session-ID: aus opts/`?session=` (Interview/Observe-Link) — oder im
  // Beobachtungs-Modus selbst erzeugt (kein vorab angelegter Proband nötig).
  let sessionId = explicitSession;
  if (!sessionId && observe && opts.studyId) {
    sessionId = ensureObserveSession(opts.studyId);
  }

  if (!opts.studyId || !sessionId || !relayUrl) {
    console.warn(
      "[userlab] init übersprungen: studyId, sessionId (oder ?session= / ?observe=1) und relayUrl erforderlich.",
      { studyId: opts.studyId, sessionId, relayUrl, observe }
    );
    return;
  }

  started = true;

  const wsUrl =
    relayUrl.replace(/\/$/, "") +
    `/ingest?session=${encodeURIComponent(sessionId)}` +
    `&study=${encodeURIComponent(opts.studyId)}` +
    (key ? `&token=${encodeURIComponent(key)}` : "");

  // Bidirektionaler Kanal vom Relay:
  // - `config`: Studien-Fragen → Frage-Modal vorbereiten. Im Observe-Modus zusätzlich
  //   die Vorab-Fragen sofort zeigen + Selbst-Abschluss-Button anbieten.
  // - `ended`: der Moderator hat die Session beendet → dem Probanden die
  //   Abschlussfragen (post_task/post_session) stellen (gilt in beiden Modi).
  const onServerMessage = (msg: unknown) => {
    const m = msg as { k?: string; studyName?: string; questions?: Question[]; locale?: string };
    if (m?.k === "config" && Array.isArray(m.questions) && m.questions.length && !questionsHandle) {
      // Forscher-Override (study.locale aus config) hat Vorrang vor Auto, sofern
      // keine explizite init/data-locale gesetzt ist.
      const qLocale = resolveLocale({ explicit: opts.locale, config: m.locale });
      questionsHandle = attachQuestions({
        questions: m.questions,
        studyName: m.studyName,
        onAnswers: (answers) => transport?.send({ k: "answers", answers }),
        withFinishButton: observe, // Selbst-Abschluss nur unmoderiert
        t: makeT(qLocale),
      });
      if (observe) questionsHandle.showPre();
    } else if (m?.k === "ended") {
      questionsHandle?.showEnd();
    }
  };

  transport = new Transport(wsUrl, dbg, onServerMessage);
  transport.connect();

  // 1) Kontext zuerst — der Relay kennt die Session, bevor Events kommen.
  //    `observe` lässt den Relay eine fehlende Session sauber selbst anlegen.
  transport.send({ k: "hello", sessionId, studyId: opts.studyId, meta: buildMeta(), observe });

  // 1b) Einstiegs-URL als eigenes Interaktions-Event festhalten. SPA-Wechsel
  //     liefern später `route_change`; ein Full-Page-Load startet das SDK aber neu
  //     und überschreibt nur `hello.meta.href` auf der Session — die Zwischen-URLs
  //     gingen so verloren. Dieses `navigate` legt JEDEN Seitenaufruf als
  //     auswertbare URL in den interactions-Stream (Basis der Auto-Erfolgsmessung).
  {
    const navEvent = { t: Date.now(), type: "navigate" as const, data: { to: location.href } };
    pushTrail(navEvent);
    transport.send({ k: "interaction", e: navEvent });
  }

  // 2) rrweb-Aufzeichnung (US1.3) mit Default-Maskierung.
  const buffer: unknown[] = [];
  // Obergrenze der Live-Latenz: ein Event ist nach spätestens FLUSH_MS beim
  // Observer (Ø FLUSH_MS/2). Bursts (initialer Snapshot, schnelle Interaktion)
  // gehen bei MAX_BATCH sofort raus, statt auf den nächsten Tick zu warten.
  const FLUSH_MS = opts.flushMs ?? 200;
  const MAX_BATCH = 64;
  const flush = () => {
    if (buffer.length === 0 || !transport) return;
    const events = buffer.splice(0, buffer.length);
    transport.send({ k: "rrweb_batch", events });
  };
  const stop = record({
    emit(event) {
      buffer.push(event);
      if (buffer.length >= MAX_BATCH) flush();
    },
    maskAllInputs: opts.maskAllInputs ?? true,
    maskTextClass: "rr-mask",
    blockClass: "rr-block",
    ignoreClass: "rr-ignore",
    recordCanvas: false,
    sampling: opts.sampling,
  });
  stopRecord = typeof stop === "function" ? stop : undefined;

  flushTimer = setInterval(flush, FLUSH_MS);

  // Beim Verlassen der Seite final flushen.
  window.addEventListener("pagehide", flush);
  window.addEventListener("beforeunload", flush);

  // 3) High-Level-Events + Frust-/Fehler-Marker (US1.2 / US1.4).
  detachInteractions = attachInteractions({
    onInteraction: (e) => {
      pushTrail(e); // Weg dahin für Kommentar-Kontext mitschreiben
      transport?.send({ k: "interaction", e });
    },
    onMarker: (e) => transport?.send({ k: "marker", e }),
    // Gleicher Mirror wie beim Replayer im Tool -> Klick-Punkte sind dort re-projizierbar.
    getNodeId: (node) => record.mirror.getId(node),
  });

  // 4) Optionales Figma-artiges Element-Feedback (pro Studie aktivierbar).
  if (opts.comments) {
    detachComments = attachComments({
      onComment: (e) => transport?.send({ k: "comment", e }),
      getNodeId: (node) => record.mirror.getId(node),
      getState: () => lastReportedState,
      getTrail: () => trail.slice(),
      t: makeT(baseLocale),
    });
  }

  log("gestartet", { sessionId, studyId: opts.studyId, relayUrl, comments: !!opts.comments });
}

/**
 * Prototyp meldet seinen aktuellen sichtbaren Zustand (Epic-10-Contract). Wird in
 * Feedback-Kommentaren als `context.state` gespeichert, damit nachvollziehbar ist,
 * in welchem Zustand der Proband kommentiert hat.
 */
export function reportState(state: unknown): void {
  lastReportedState = state;
}

/**
 * Eigenes Ereignis auf der Zeitachse markieren (US1.5 / Custom-Events).
 */
export function track(name: string, payload?: Record<string, unknown>): void {
  transport?.send({
    k: "marker",
    e: { t: Date.now(), type: "custom", payload: { name, ...payload } },
  });
}

/** Aufzeichnung beenden und Ressourcen freigeben. */
export function stop(): void {
  if (!started) return;
  stopRecord?.();
  detachInteractions?.();
  detachComments?.();
  questionsHandle?.cleanup();
  if (flushTimer) clearInterval(flushTimer);
  transport?.close();
  started = false;
  log("gestoppt");
}

// ── Auto-Init bei <script data-study="…" data-relay="…"> ────────────────
try {
  if (typeof document !== "undefined") {
    const el = (document.currentScript ||
      document.querySelector("script[data-study]")) as HTMLScriptElement | null;
    const studyId = el?.dataset.study;
    if (studyId) {
      init({
        studyId,
        relayUrl: el?.dataset.relay,
        observe: el?.dataset.observe === "true" ? true : undefined,
        comments: el?.dataset.comments === "true",
        locale: el?.dataset.locale,
        debug: el?.dataset.debug === "true",
      });
    }
  }
} catch {
  /* Auto-Init ist optional */
}
